import type {ReactElement} from 'react';
import React from 'react';
import type {FillObject} from '@PosterWhiteboard/classes/fill.class';
import {FillTypes} from '@PosterWhiteboard/classes/fill.class';
import type {ColorFillStorage} from '@Components/contextual-menu/components/contextual-menu-color-fill-option';
import {ContextualMenuColorFillOption} from '@Components/contextual-menu/components/contextual-menu-color-fill-option';
import {getColorObjectArrayFromFillColor} from '@Utils/color.util';
import {ColorFillPropertyType} from '@Components/color-fill';
import {BaseVectorColorOpacityOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-vector-color-opacity-option';
import {ContextualMenuToggleOption} from '@Components/contextual-menu/components/contextual-menu-toggle-option';
import {BaseVectorRoundnessOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-vector-roundness-option/base-vector-roundness-option';

interface BaseVectorColorOptionProps {
  fillObject: FillObject;
  onChangeFillType(newType: FillTypes): void;
  onClickColorFill(): void;
  onChangeOpacity(updatedValue: number, undoable?: boolean): void;
  label?: string;
  showRoundnessOption?: boolean;
  fillRoundness?: number;
  onChangeRoundness?(updatedValue: number, undoable?: boolean): void;
}

enum VectorFillColorType {
  NONE = 'none',
  COLOR = 'color',
}

export function BaseVectorColorOption({
  fillRoundness = 0,
  label = window.i18next.t('pmwjs_color'),
  showRoundnessOption = false,
  ...props
}: BaseVectorColorOptionProps): ReactElement {
  const getSelectedItemId = (): VectorFillColorType => {
    if (props.fillObject.fillType === FillTypes.NONE) {
      return VectorFillColorType.NONE;
    }
    return VectorFillColorType.COLOR;
  };

  const isToggled = (): boolean => {
    return props.fillObject.fillType !== FillTypes.NONE;
  };

  const getColorOpacityOption = (): ReactElement => {
    return (
      <BaseVectorColorOpacityOption
        fillAlpha={props.fillObject.fillColor[0][3]}
        onChangeOpacity={(val: number, undoable = true): void => {
          props.onChangeOpacity(val, undoable);
        }}
      />
    );
  };

  const getFillRoundnessOption = (): ReactElement => {
    return (
      <BaseVectorRoundnessOption
        fillRoundness={fillRoundness}
        onChangeRoundness={(val: number, undoable = true): void => {
          if (props.onChangeRoundness) {
            props.onChangeRoundness(val, undoable);
          }
        }}
      />
    );
  };

  const getSubOptions = (): ReactElement | null => {
    if (getSelectedItemId() !== VectorFillColorType.NONE) {
      return (
        <>
          {getColorFillOption()}
          {getColorOpacityOption()}
          {showRoundnessOption ? getFillRoundnessOption() : null}
        </>
      );
    }
    return null;
  };

  const getFillType = (): ColorFillPropertyType => {
    if (props.fillObject.fillType === FillTypes.LINEAR_GRADIENT) {
      return ColorFillPropertyType.LINEAR;
    }

    if (props.fillObject.fillType === FillTypes.RADIAL_GRADIENT) {
      return ColorFillPropertyType.RADIAL;
    }

    return ColorFillPropertyType.SOLID;
  };

  const getColorObject = (): ColorFillStorage[] => {
    return [
      {
        id: 'color-fill-vector',
        fillType: getFillType(),
        color: getColorObjectArrayFromFillColor(props.fillObject.fillColor),
      },
    ];
  };

  const onClickColorFill = (): void => {
    props.onClickColorFill();
  };

  const onToggleHandler = (): void => {
    if (props.fillObject.fillType === FillTypes.SOLID) {
      props.onChangeFillType(FillTypes.NONE);
    } else {
      props.onChangeFillType(FillTypes.SOLID);
    }
  };

  const getColorFillOption = (): ReactElement => {
    return <ContextualMenuColorFillOption id="vector-fill-color-option" label={window.i18next.t('pmwjs_color')} data={getColorObject()} onClick={onClickColorFill} />;
  };

  return (
    <ContextualMenuToggleOption id="vector-color-option-toggle" label={label} isChecked={isToggled()} onClick={onToggleHandler}>
      {getSubOptions()}
    </ContextualMenuToggleOption>
  );
}
